import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "AWS, Azure, Vercel, Netlify, or Firebase?",
  "description": "How do you choose between serverless providers?",
  "image": "./img/serverless-flavors.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "aws-azure-vercel-netlify-or-firebase"
    }}>{`AWS, Azure, Vercel, Netlify, or Firebase?`}</h1>
    <p><img parentName="p" {...{
        "src": "/d5be0bc4c11b71ad551499a0cb04d820/serverless-flavors.svg",
        "alt": null
      }}></img></p>
    <p>{`You've read the `}<a parentName="p" {...{
        "href": "/getting-started"
      }}>{`getting started`}</a>{` of serverless, you know the `}<a parentName="p" {...{
        "href": "/serverless-pros-cons"
      }}>{`pros and cons`}</a>{`, and decided to use serverless in your next project.`}</p>
    <p>{`Now what?`}</p>
    <p>{`There's a bunch of providers with different features, different pricing, different developer experience, different focus ... how do you choose?`}</p>
    <p>{`Having tried many of those providers myself (AWS, Netlify, Vercel, Firebase), here's how I think about it 👇`}</p>
    <h2 {...{
      "id": "aws"
    }}>{`AWS`}</h2>
    <p><a parentName="p" {...{
        "href": "https://aws.amazon.com"
      }}><span parentName="a" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px",
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "60.53811659192825%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'243\\'%20viewBox=\\'0%200%20400%20243\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M0%20122v121h401V0H0v122m148-88l-16%201c-2%202-2%2029%201%2031%202%202%2029%203%2031%201l1-10v-8h5l6-1h-11v-6c0-8-1-9-17-8m36%201l-1%2014v13h-5c-9%200-9%200-9%2013l1%2013%2013%201c13%200%2013%200%2013-13V66h13v3l2%204v-1c-2-2-1-5%201-7%202%200%203-2%203-3%200-2%201-3%204-3l4-1h-8V48c0-15%201-14-16-14l-15%201m-51%202v15l1%2014h29V45l-5-4-4-5h-10l-11%201m52%2012v14l14%201h14l-1-9c0-9%200-9-4-12l-3-6c1-1-1-1-9-1h-11v13m11-8l-2%206-2%205c-1%202%201%208%202%206l5-1%205%201c1%201%201%201%202-1%201-4-4-19-7-19l-3%203m-53-1l-2%202-2%202-1%202v10l1%202%202%202%202%201h10l2-1%202-2%202-2-1-1c-1-2-1-2%201-3v-2l-1-1%201-2v-1l-1-2c0-2-1-3-3-3l-2-1h-1l-1%201-2-1c0-1-1-1-2%201h-3l-1-1m-3%2011v8h16V43h-16v8m116%2013v5h-32v-5l-1-4v4c0%205%200%205-3%205-6%200-7%201-7%2017v13h-25c-25%200-26%200-27%202l-1%2028v27h-3l-3%201h3l3%201c0%203%203%205%207%205h3v11h-8l-8%201h17v-12h47v8l1%209h10c15%200%2014%201%2014-12%200-14%200-14-11-14h-8v-36h17c15%200%2018%200%2019-2%202-1%202-4%202-22%200-22%200-25-4-25l-1-5-1-5v5m-85%2012v11h11c13%200%2012%200%2011-10-1-8-3-11-6-11-2%200-2%200%200%201s2%201%202%207c-1%208-3%2011-7%2011-2%200-2%200-1-1V68c-4-3-6%209-2%2014v2c-2%200-4-6-4-11%200-4%201-6%206-7s2-2-4-2h-6v12m141-10c-2%201-2%204-2%2013%200%2014%200%2014%2015%2014%2014%200%2014%200%2014-15l-1-13c-2-2-24-1-26%201M85%2069v3H74l-13%201v30c1%202%202%202%2015%202l16-1c1-2%202-30%200-31l-3-1c-2%200-3-1-3-3l-1-3v3m227-2v13l1%2011h24V74l-4-4-3-4h-9l-9%201m-96%205l-1%2014v13h3c5%200%206%201%206%2010v7h36l-1-15V86l-7-7-6-7-15-1-15%201M62%2075v15l1%2013h28V92l-1-10-2-1c-1-1-1%201-1%207%200%2010-1%2011-11%2011-8%200-9-1-9-10%200-10%201-11%209-12%205%200%207-1%207-2%200-2-20-2-21%200m168%205c-4%201-7%203-7%204l5-2c4-2%2012-3%2017-1%203%201%203%201%202%204-1%202-2%203-4%202-4-2-9-1-14%201l-4%203-2-2c-2-2-2-3-1-4v-1c-2%200-2%203%200%206l3%203%204-3c4-2%208-3%2013-2l2%201c0%202-6%2011-7%2012-5%202-4%208%201%208%203%200%205-5%203-8v-3a5004%205004%200%20007-7c2%201%202%201%205-2l3-2-3-4c-6-5-15-6-23-3m51%206c0%205%200%205-4%205l-3%201-1%2011c0%2014-1%2014%2013%2014%2013%200%2013%200%2013-13%200-12-2-25-2-18v5h-15v-5l-1-4v4m-158%203c-2%201-3%2027-1%2030%201%202%202%202%2016%202l16-1V89h-31m1%2015v15h29v-10l-1-10-4-4-4-4-10-1h-10v14m8-9c-6%205-7%2013-2%2018%207%207%2019%203%2020-8%200-5%200-5-3-8-5-5-11-5-15-2m143%209v11h22v-7c0-7%200-7-4-11-3-4-4-4-11-4h-7v11m-113-2v30l1%2029h59v-38l-5-4-11-11-7-7h-18l-19%201m-129%209l1%209h5c6%200%207%202%203%206-2%202-2%203-2%2013%200%2013%201%2015%206%2015h4v5l1%205h15l-7-1h-7l-1-5v-4h8c11%201%2011%201%2011-14%200-14%200-15-7-15h-4v-5c0-5-1-6-4-6s-3%200-3-6c-1-4-1-4-1%203%200%204%200%207%201%206%200-2%201-2%203-2s3%201%203%205v5h-6c-7%200-7%200-7-2%200-3%201-3%203-3l-5-1h-8l-1-8-1-8v8m152-2c-18%207-20%2033-3%2042%2023%2012%2045-19%2026-37-6-5-15-7-23-5m1%203c-24%207-15%2044%2010%2039%2021-4%2022-33%202-40-6-1-6-1-12%201m0%207l-6%204-1%208v8l7%203%206%204%203-2%207-4%204-2v-7c0-9%201-7-10-14l-4-2-6%204m2%202c-6%202-6%204%201%206%204%202%204%202%207%200l4-2-2-2c-5-5-5-5-10-2m-7%2011v6l5%202%205%203v-12l-4-2c-6-4-6-4-6%203m17-3l-5%202v6c0%207%200%207%206%203%204-3%205-3%205-8%200-7%200-7-6-3m-156%200v13l1%2010h12c14%200%2014%201%2013-10-1-7-1-8-6-11-4-4-4-4-12-4-6%200-7%200-8%202m8%205c-5%205-5%205-1%2010%205%205%207%205%2012%200l4-4-10-10-5%204m230%2012v8h-5a172%20172%200%2001-11%201l3%201c2%200%202%200%202%208v9h-8c-6%200-8%201-9%202h1l9-1%207%201v5l1%205h13l16-1c1-2%202-27%200-29l-10-1h-8v-8l-1-8v8m-263-1v6l1%205h11v-11l-6-1-6%201m93%201v29c1%202%202%202%2015%202l16-1%201-16v-15l-16-1c-14%200-15%200-16%202m1%201v15l1%2013h28v-11l-1-11-4-3-4-4h-10l-10%201m113%2011l-2%204c-2%201-2%203-2%209v7h21v-8l-1-8c0%201-2%200-3-2l-8-4c-5%200-5%200-5%202m47%2012v13h27l-1-9v-9l-4-5-4-4h-18v14m10-8l-5%201c-1-1-2%200-2%203-1%205-1%205%204%205s6%200%204-2%202-5%207-5c4%200%204-1%200-3-3-1-5-1-8%201m-51%201l-2%202h-3c-3%201-3%202-3%205v4h17v-4c0-4-1-4-3-5l-4-3c-2-1-2-1-2%201m59%205c-3%200-3%200-2%202%200%203-3%205-8%205-2%200-2%200-1%201%202%203%206%203%2010%202%202-1%204-2%205-1%202%200%202%200%202-4v-5h-6\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/0725f2b1cf8f75be96d672d160cb5e22/ca0a1/aws.webp 223w", "/static/0725f2b1cf8f75be96d672d160cb5e22/75680/aws.webp 445w", "/static/0725f2b1cf8f75be96d672d160cb5e22/8d1ba/aws.webp 890w", "/static/0725f2b1cf8f75be96d672d160cb5e22/928c4/aws.webp 1120w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/0725f2b1cf8f75be96d672d160cb5e22/e92b6/aws.png 223w", "/static/0725f2b1cf8f75be96d672d160cb5e22/e66bf/aws.png 445w", "/static/0725f2b1cf8f75be96d672d160cb5e22/4ef49/aws.png 890w", "/static/0725f2b1cf8f75be96d672d160cb5e22/f43e4/aws.png 1120w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/0725f2b1cf8f75be96d672d160cb5e22/4ef49/aws.png",
              "alt": "aws",
              "title": "aws",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
    `}</span></a></p>
    <p>{`AWS is the serverless workhorse. They offer everything from function-as-a-service to hosted blockchain and machine learning products.`}</p>
    <p>{`Many other hosting providers use AWS. Heroku runs their dynos on EC2 instances, Netlify and Vercel use S3 for static files, Lambda for cloud functions, etc. The exact details are a secret, but we can guess.`}</p>
    <p>{`Did you know AWS was `}<a parentName="p" {...{
        "href": "https://www.itproportal.com/news/aws-now-makes-up-over-half-of-all-amazon-revenue/"
      }}>{`more than half of Amazon's revenue`}</a>{` in 2019? It's a beast.`}</p>
    <p>{`With over 165 services, it's impossible to try or even know all of AWS. A few that I've used are:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`EC2`}</strong>{` – old school cloud. You get a virtual computer, set it up, and you're in control. Runs forever unless you make it stop.`}</li>
      <li parentName="ul"><strong parentName="li">{`S3`}</strong>{` – the standard solution for static files. Upload a file, get a URL, file stays there forever. Used for image and video assets, but can't run server code or host a website.`}</li>
      <li parentName="ul"><strong parentName="li">{`CloudFront`}</strong>{` – a `}<a parentName="li" {...{
          "href": "https://en.wikipedia.org/wiki/Content_delivery_network"
        }}>{`CDN`}</a>{` that integrates with S3. Point to static files via CloudFront and they go to a server nearest to your users. Works like a read-through cache, makes your apps faster.`}</li>
      <li parentName="ul"><strong parentName="li">{`IAM`}</strong>{` – identity and account management. AWS forces you to use this to manage permissions. It's secure, tedious to set up, and a thorn in your butt. Until it saves your butt.`}</li>
      <li parentName="ul"><strong parentName="li">{`AWS Secrets Manager`}</strong>{` – a secure way to use secrets in your serverless apps. Not in code, not in environment variables, but a secure encrypted storage. `}</li>
      <li parentName="ul"><strong parentName="li">{`Lambda`}</strong>{` – the poster child for serverless. One of the first to popularize function-as-a-service architectures. Write your function, Lambda handles the rest.`}</li>
      <li parentName="ul"><strong parentName="li">{`SQS`}</strong>{` – simple queue service is a simple way to implement queues. It's best used for communication between services. Stores and retries messages if necessary.`}</li>
      <li parentName="ul"><strong parentName="li">{`SNS`}</strong>{` – simple notification service. Similar to SQS but designed for broadcasting. Many listeners can read each message and every message is delivered just once.`}</li>
      <li parentName="ul"><strong parentName="li">{`DynamoDB`}</strong>{` – a hosted JSON document storage. One of the quickest-to-setup ways to persist data in a serverless application. Save a JSON blob and read it later. Not recommended for relational data models.`}</li>
      <li parentName="ul"><strong parentName="li">{`RDS`}</strong>{` – relational database service. Set up a Postgres or MySQL database and AWS handles the rest. Better fit for traditional data with lots of relations. `}</li>
      <li parentName="ul"><strong parentName="li">{`CloudWatch`}</strong>{` – a logging system. Every service in AWS connects to CloudWatch to dump logs and other debug data. You can use it to trigger lambdas on a schedule.`}</li>
    </ul>
    <p>{`AWS services add up fast. Every tool does `}<em parentName="p">{`one`}</em>{` job. No tool does `}<em parentName="p">{`your`}</em>{` job.`}</p>
    <h3 {...{
      "id": "when-to-choose-aws"
    }}>{`When to choose AWS`}</h3>
    <p>{`I use AWS when I want control over the developer experience and how the system fits together. `}</p>
    <p>{`For example: I'd use AWS when my project involves data pipelines, coordinating between users, and complex backend logic. You know it's backend logic because it impacts multiple users on different devices.`}</p>
    <h3 {...{
      "id": "when-not-to-choose-aws"
    }}>{`When not to choose AWS`}</h3>
    <p>{`Where AWS becomes overkill are typical JAMstack apps. A static site with lots of frontend logic.`}</p>
    <p>{`Hosting those on AWS is a pain whereas Netlify and Vercel make them a core feature.`}</p>
    <h2 {...{
      "id": "azure"
    }}>{`Azure`}</h2>
    <p><a parentName="p" {...{
        "href": "https://azure.microsoft.com"
      }}><span parentName="a" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px",
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "68.16143497757847%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'272\\'%20viewBox=\\'0%200%20400%20272\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M0%20136v136h401V0H0v136m198-97c-28%206-48%2030-48%2057s18%2051%2045%2058c5%202%205%202-9%202-10%200-15%200-13-1s1-1-3-1l-2-1-1-1-5-3-4-3-2-2-2-4-4-5c-2-7-2-7-13-7h-9v-18a363%20363%200%20001-31c1-1-1-1-5-1-15%200-35%208-48%2021a77%2077%200%2000-21%2082c6%2019%2021%2036%2039%2045%2019%209%2012%209%20116%209h91l6-3c23-8%2037-29%2036-53-1-7-3-16-3-11l-64%201-65%201a2060%202060%200%20011-69l2%202%204%201c2%202%205%203%2014%203l7%201c-7%2013-9%2024-4%2033%2012%2026%2049%2023%2057-4%201-5%201-14-1-11l-11%201c-13-1-15-2-14-4a61%2061%200%2000-68-84\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/4cf336e5d9bd6948706e2a79705a0093/ca0a1/azure.webp 223w", "/static/4cf336e5d9bd6948706e2a79705a0093/75680/azure.webp 445w", "/static/4cf336e5d9bd6948706e2a79705a0093/8d1ba/azure.webp 890w", "/static/4cf336e5d9bd6948706e2a79705a0093/7592c/azure.webp 1188w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/4cf336e5d9bd6948706e2a79705a0093/e92b6/azure.png 223w", "/static/4cf336e5d9bd6948706e2a79705a0093/e66bf/azure.png 445w", "/static/4cf336e5d9bd6948706e2a79705a0093/4ef49/azure.png 890w", "/static/4cf336e5d9bd6948706e2a79705a0093/df56e/azure.png 1188w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/4cf336e5d9bd6948706e2a79705a0093/4ef49/azure.png",
              "alt": "azure",
              "title": "azure",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
    `}</span></a></p>
    <p>{`Azure ... exists. Microsoft's answer to AWS and it's hard to find people in the wild who use it. `}</p>
    <p>{`Popular in the enterprise world with companies that can't or won't use Amazon services. I would stay away unless there's a good reason.`}</p>
    <h2 {...{
      "id": "firebase"
    }}>{`Firebase`}</h2>
    <p><a parentName="p" {...{
        "href": "https://firebase.google.com/"
      }}><img parentName="a" {...{
          "src": "../..images/firebase.png",
          "alt": null
        }}></img></a></p>
    <div id="lock" />
    <p>{`Firebase is Google's answer to AWS. Kind of.`}</p>
    <p>{`You can think of Firebase as a done-for-you backend for web and mobile apps. Set up an account, pick your options, and it's ready to go.`}</p>
    <p>{`Right now there are 18 firebase services ranging from analytics to database and even machine learning. I've tried their database solution and it works great.`}</p>
    <p>{`This is less extensive than the AWS section and that's the point. Firebase doesn't want you to think about any of this.`}</p>
    <h3 {...{
      "id": "when-to-choose-firebase"
    }}>{`When to choose Firebase`}</h3>
    <p>{`You get the backend in a box with Firebase. There's little setup.`}</p>
    <p>{`You'll have to change how you write your frontend code so it hooks up with Firebase and ... that's about it. `}</p>
    <p>{`Great for small demos and when you don't want to think about the backend at all.`}</p>
    <h3 {...{
      "id": "when-not-to-choose-firebase"
    }}>{`When not to choose Firebase`}</h3>
    <p>{`Like with other Magic providers, you're in trouble as soon as you step off the beaten path. Or when you have a special requirement they didn't predict.`}</p>
    <p>{`When that happens your choice is to either change your app or rebuild from scratch.`}</p>
    <h2 {...{
      "id": "netlify"
    }}>{`Netlify`}</h2>
    <p><a parentName="p" {...{
        "href": "https://www.netlify.com/"
      }}><span parentName="a" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px",
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "80.26905829596413%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'321\\'%20viewBox=\\'0%200%20400%20321\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M0%20131v131h3l61-5a514%20514%200%2000182-58l6-3-10-14-10-16c1-1%2051-31%2053-31l4%203%203%204%201%202c-1%201%2010%2024%2011%2023a1946%201946%200%200082-48l14-8v75c0%2067%201%2061%201-55V0H0v131m261%2022l-23%2015%2017%2026%2043-24c1-1-12-29-14-30l-23%2013m35%2032l-3%203%202%202c4%204%209%205%2013%205%203-1%2019%201%2021%203l-6%2010-4%205-4-2c-3-2-7-3-12-3h-8l-1%205-2%2011c0%208-2%209-13%207l-8-1v-8c1-6%202-10%204-13%203-7%204-12%202-11-1%200-2-1-1-3l-1-3-1%203-1%202-1-2c1-3-1-3-4%200-2%203-2%203-1%208s1%206-3%2014c-7%2017-6%2022%208%2029l12%208c6%207%2014%2010%2020%2010%209-1%2030-12%2035-20%202-4%204-14%202-20l1-8%201-13c-1-12-3-13-20-14-10-1-12-1-17-4-6-4-6-4-10%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/a9229dffd6e637ef664682265109311e/ca0a1/netlify.webp 223w", "/static/a9229dffd6e637ef664682265109311e/75680/netlify.webp 445w", "/static/a9229dffd6e637ef664682265109311e/8d1ba/netlify.webp 890w", "/static/a9229dffd6e637ef664682265109311e/24091/netlify.webp 1262w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/a9229dffd6e637ef664682265109311e/e92b6/netlify.png 223w", "/static/a9229dffd6e637ef664682265109311e/e66bf/netlify.png 445w", "/static/a9229dffd6e637ef664682265109311e/4ef49/netlify.png 890w", "/static/a9229dffd6e637ef664682265109311e/62da8/netlify.png 1262w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/a9229dffd6e637ef664682265109311e/4ef49/netlify.png",
              "alt": "netlify",
              "title": "netlify",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
    `}</span></a></p>
    <p>{`Netlify is wonderful. They invented everyone's favorite `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Netlify#JAMstack"
      }}>{`JAMstack`}</a>{` buzzword. `}</p>
    <p>{`Starting as a static site host for JavaScript-heavy applications, Netlify now offers cloud functions, authentication, and other backend functionality. Meant for simple backend tasks.`}</p>
    <p>{`Among Netlify's biggest draws are:`}</p>
    <ul>
      <li parentName="ul">{`a great web UI to manage your projects`}</li>
      <li parentName="ul">{`git integration for automated deploys`}</li>
      <li parentName="ul">{`custom URLs for every branch`}</li>
      <li parentName="ul">{`automatic CDN and SSL setup for your site`}</li>
      <li parentName="ul">{`app builds run on their servers`}</li>
      <li parentName="ul">{`you can run their infrastructure locally`}</li>
      <li parentName="ul">{`PR deploy previews`}</li>
      <li parentName="ul">{`great support for teams`}</li>
    </ul>
    <h3 {...{
      "id": "when-to-choose-netlify"
    }}>{`When to choose Netlify`}</h3>
    <p>{`Netlify is one of the best funded startups in this arena. That can be an important factor.`}</p>
    <p>{`They've been around long enough to rely on and are young enough that you'll get decent support, if you have issues. I find that to be a great balance :)`}</p>
    <p>{`If you like a git- and UI- first mentality, Netlify is great.`}</p>
    <h3 {...{
      "id": "when-not-to-choose-netlify"
    }}>{`When not to choose Netlify`}</h3>
    <p>{`Netlify is almost always a great choice for your web app. Their cloud function support can be cumbersome and malnourished. It doesn't feel like a focus.`}</p>
    <p>{`If you like interacting with your sites via the command line, I've found Netlify to be less great.`}</p>
    <h2 {...{
      "id": "vercel"
    }}>{`Vercel`}</h2>
    <p><a parentName="p" {...{
        "href": "https://vercel.com/"
      }}><span parentName="a" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px",
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "58.29596412556054%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'234\\'%20viewBox=\\'0%200%20400%20234\\'%20preserveAspectRatio=\\'none\\'/%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/0c1088a170e9bd949d0263d633c3638b/ca0a1/zeit.webp 223w", "/static/0c1088a170e9bd949d0263d633c3638b/75680/zeit.webp 445w", "/static/0c1088a170e9bd949d0263d633c3638b/8d1ba/zeit.webp 890w", "/static/0c1088a170e9bd949d0263d633c3638b/6f638/zeit.webp 1288w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/0c1088a170e9bd949d0263d633c3638b/e92b6/zeit.png 223w", "/static/0c1088a170e9bd949d0263d633c3638b/e66bf/zeit.png 445w", "/static/0c1088a170e9bd949d0263d633c3638b/4ef49/zeit.png 890w", "/static/0c1088a170e9bd949d0263d633c3638b/01a87/zeit.png 1288w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/0c1088a170e9bd949d0263d633c3638b/4ef49/zeit.png",
              "alt": "zeit",
              "title": "zeit",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
    `}</span></a></p>
    <p>{`Vercel is wonderful. Netlify's direct competitor created by Silicon Valley veterans.`}</p>
    <p>{`Vercel's focus, however, is different than Netlify's. `}</p>
    <p>{`Where Netlify makes static deploys painless to configure and deploy via git and their UI, Vercel tries to make `}<em parentName="p">{`any`}</em>{` app painless to deploy via their CLI. Command-line first, git and UI second.`}</p>
    <p>{`Vercel's cloud function support is similar to Netlify's. Meant for small functionality and help with frontend tasks. `}</p>
    <p>{`Among Vercel's biggest draws are:`}</p>
    <ul>
      <li parentName="ul">{`superb command line interface`}</li>
      <li parentName="ul">{`configure-less deploys`}</li>
      <li parentName="ul">{`git integration for automated deploys`}</li>
      <li parentName="ul">{`custom URLs for every deploy`}</li>
      <li parentName="ul">{`automatic CDN and SSL setup for your site`}</li>
      <li parentName="ul">{`PR deploy previews`}</li>
      <li parentName="ul">{`app builds run on their servers`}</li>
      <li parentName="ul">{`great support for teams`}</li>
    </ul>
    <h3 {...{
      "id": "when-to-choose-vercel"
    }}>{`When to choose Vercel`}</h3>
    <p>{`Vercel is almost always a great choice for your web app. `}</p>
    <p>{`Honestly it's a toss up between Netlify and Vercel right now. They're both playing catch up. When one releases a new feature, the other gets it a few months later.`}</p>
    <p>{`I like Vercel's command line interface and the fact I can run `}<inlineCode parentName="p">{`vercel`}</inlineCode>{` in any project and it shows up on the internet. No clicking or config needed.`}</p>
    <h3 {...{
      "id": "when-not-to-choose-vercel"
    }}>{`When not to choose Vercel`}</h3>
    <p>{`Vercel is best for frontend-heavy apps and when you're using their NextJS framework. Like Netlify, it feels unlikely their backend support will reach the full power of AWS.`}</p>
    <p>{`Any project that fits on Netlify, fits on Vercel. Then it becomes a matter of preference and familiarity. Personally, I like Vercel's aesthetic and CLI focus. If you prefer clicking in a UI, go with Netlify.`}</p>
    <h2 {...{
      "id": "so--what-to-choose"
    }}>{`So ... what to choose?`}</h2>
    <p>{`My preference is to put the frontend on Netlify or Vercel and the backend on AWS.`}</p>
    <p>{`This gives me a balance between control, simplicity, and developer experience. We look at that in the next chapter.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      